import { useState, useEffect, useRef } from 'react';
import PhotoUploader from '../components/PhotoUploader';

const API = '/api/public';

export default function PublicRequestPage() {
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [successCallId, setSuccessCallId] = useState(null);

  // Property selection
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [propertySearch, setPropertySearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Form fields
  const [callType, setCallType] = useState('Space Call');
  const [customCallType, setCustomCallType] = useState('');
  const [callReason, setCallReason] = useState('Reserved Space');
  const [customCallReason, setCustomCallReason] = useState('');
  const [spaceNumber, setSpaceNumber] = useState('');
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleColor, setVehicleColor] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [proofPhotos, setProofPhotos] = useState([]);
  const [violationPhotos, setViolationPhotos] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const formRef = useRef(null);
  const dropdownRef = useRef(null);

  // Load properties on mount
  useEffect(() => {
    fetch(`${API}/properties`)
      .then(r => r.json())
      .then(data => setProperties(data))
      .catch(() => setProperties([]));
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter properties for searchable dropdown
  const filteredProperties = properties.filter(p =>
    !propertySearch || p.name.toLowerCase().includes(propertySearch.toLowerCase())
  );

  function selectProperty(p) {
    setPropertyId(String(p.id));
    setPropertySearch(p.name);
    setShowDropdown(false);
  }

  // Submit call
  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError('');

    // Client-side validation
    if (!propertyId) return setSubmitError('Please select a property.');
    if (!contactName.trim()) return setSubmitError('Contact name is required.');
    if (!contactNumber.trim()) return setSubmitError('Contact number is required.');
    if (!vehicleMake.trim() && !vehicleModel.trim() && !vehicleColor.trim() && !vehiclePlate.trim()) {
      return setSubmitError('At least one vehicle detail is required.');
    }
    if (violationPhotos.length === 0) {
      return setSubmitError('At least one photo of the vehicle in violation is required.');
    }
    if (callType === 'Other' && !customCallType.trim()) {
      return setSubmitError('Please describe the call type.');
    }
    if (callReason === 'Other' && !customCallReason.trim()) {
      return setSubmitError('Please describe the call reason.');
    }
    if (callReason === 'Reserved Space' && !spaceNumber.trim()) {
      return setSubmitError('Reserved space number is required.');
    }

    setSubmitLoading(true);

    const fd = new FormData();
    fd.append('property_id', propertyId);
    fd.append('call_type', callType);
    fd.append('call_reason', callReason);
    if (callType === 'Other') fd.append('custom_call_type', customCallType.trim());
    if (callReason === 'Other') fd.append('custom_call_reason', customCallReason.trim());
    fd.append('vehicle_make', vehicleMake.trim());
    fd.append('vehicle_model', vehicleModel.trim());
    fd.append('vehicle_color', vehicleColor.trim());
    fd.append('vehicle_plate', vehiclePlate.trim());
    fd.append('space_number', spaceNumber.trim());
    fd.append('contact_name', contactName.trim());
    fd.append('contact_number', contactNumber.trim());
    fd.append('notes', notes.trim());

    // Combine all photos into single 'photos' field
    for (const file of proofPhotos) fd.append('photos', file);
    for (const file of violationPhotos) fd.append('photos', file);

    try {
      const res = await fetch(`${API}/submit-call`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      setSuccessCallId(data.call_id);
      setStep('success');
    } catch (err) {
      setSubmitError(err.message);
    }
    setSubmitLoading(false);
  }

  function handleReset() {
    setStep('form');
    setPropertyId('');
    setPropertySearch('');
    setCallType('Space Call');
    setCustomCallType('');
    setCallReason('Reserved Space');
    setCustomCallReason('');
    setSpaceNumber('');
    setVehicleMake('');
    setVehicleModel('');
    setVehicleColor('');
    setVehiclePlate('');
    setContactName('');
    setContactNumber('');
    setProofPhotos([]);
    setViolationPhotos([]);
    setNotes('');
    setSubmitError('');
    setSuccessCallId(null);
  }

  return (
    <div className="public-page">
      <div className="public-header">
        <div className="public-logo">
          <img src={`${process.env.PUBLIC_URL}/logo-banner.png`} alt="Apollo Towing DFW" className="public-logo-img" />
        </div>
        <p className="public-header-subtitle">Parking Violation Report</p>
      </div>
      <div className="public-watermark" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/logo-icon.png)` }} />

      <div className="public-card">
        {/* FORM */}
        {step === 'form' && (
          <>
            <div className="public-card-header">
              <h2>Report a Parking Violation</h2>
              <p>Select your property and provide the details below. All fields are required unless noted otherwise.</p>
            </div>
            <form onSubmit={handleSubmit} className="public-form" ref={formRef}>
              {submitError && <div className="public-alert public-alert-error">{submitError}</div>}

              {/* Property Search */}
              <div className="form-group" ref={dropdownRef}>
                <label>Property <span className="required">*</span></label>
                <input
                  type="text"
                  placeholder="Search for your property..."
                  value={propertySearch}
                  onChange={e => {
                    setPropertySearch(e.target.value);
                    setPropertyId('');
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  className="public-input"
                  autoComplete="off"
                />
                {showDropdown && !propertyId && (
                  <div className="public-dropdown">
                    {filteredProperties.length === 0 && (
                      <div className="public-dropdown-empty">No properties found</div>
                    )}
                    {filteredProperties.slice(0, 20).map(p => (
                      <div key={p.id} className="public-dropdown-item" onClick={() => selectProperty(p)}>
                        {p.name}
                      </div>
                    ))}
                    {filteredProperties.length > 20 && (
                      <div className="public-dropdown-empty">Type to narrow results...</div>
                    )}
                  </div>
                )}
              </div>

              {/* Call Type */}
              <div className="form-group">
                <label>Call Type <span className="required">*</span></label>
                <select value={callType} onChange={e => setCallType(e.target.value)} className="public-input">
                  <option value="Space Call">Space Call</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              {callType === 'Other' && (
                <div className="form-group">
                  <label>Describe Call Type <span className="required">*</span></label>
                  <input type="text" value={customCallType} onChange={e => setCustomCallType(e.target.value)} placeholder="Briefly describe the type of call" className="public-input" maxLength={200} />
                </div>
              )}

              {/* Call Reason */}
              <div className="form-group">
                <label>Call Reason <span className="required">*</span></label>
                <select value={callReason} onChange={e => setCallReason(e.target.value)} className="public-input">
                  <option value="Reserved Space">Reserved Space</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              {callReason === 'Other' && (
                <div className="form-group">
                  <label>Describe Reason <span className="required">*</span></label>
                  <input type="text" value={customCallReason} onChange={e => setCustomCallReason(e.target.value)} placeholder="Briefly describe the reason" className="public-input" maxLength={200} />
                </div>
              )}

              {/* Reserved Space Number */}
              {callReason === 'Reserved Space' && (
                <div className="form-group">
                  <label>Reserved Space Number <span className="required">*</span></label>
                  <input type="text" value={spaceNumber} onChange={e => setSpaceNumber(e.target.value)} placeholder="e.g. A-42, 105, etc." className="public-input" maxLength={50} />
                </div>
              )}

              {/* Vehicle Information */}
              <div className="form-group">
                <label>Vehicle Information <span className="required">*</span></label>
                <div className="public-vehicle-grid">
                  <input type="text" value={vehicleMake} onChange={e => setVehicleMake(e.target.value)} placeholder="Make (e.g. Honda)" className="public-input" maxLength={50} />
                  <input type="text" value={vehicleModel} onChange={e => setVehicleModel(e.target.value)} placeholder="Model (e.g. Civic)" className="public-input" maxLength={50} />
                  <input type="text" value={vehicleColor} onChange={e => setVehicleColor(e.target.value)} placeholder="Color (e.g. Red)" className="public-input" maxLength={30} />
                  <input type="text" value={vehiclePlate} onChange={e => setVehiclePlate(e.target.value)} placeholder="License Plate" className="public-input" maxLength={20} />
                </div>
              </div>

              {/* Contact Info */}
              <div className="form-group">
                <label>Contact Name <span className="required">*</span></label>
                <input type="text" value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Your full name" className="public-input" maxLength={100} />
              </div>
              <div className="form-group">
                <label>Contact Number <span className="required">*</span></label>
                <input type="tel" value={contactNumber} onChange={e => setContactNumber(e.target.value)} placeholder="(555) 123-4567" className="public-input" maxLength={20} />
              </div>

              {/* Proof of Space (optional) */}
              <div className="form-group">
                <label>Proof of Reserved Space <span className="optional">(Optional)</span></label>
                <p className="form-hint">Photo of reserved space sign, permit, or lease agreement.</p>
                <PhotoUploader files={proofPhotos} onChange={setProofPhotos} />
              </div>

              {/* Vehicle Photos (required) */}
              <div className="form-group">
                <label>Photos of Vehicle in Violation <span className="required">*</span></label>
                <p className="form-hint">Please include clear photos showing the vehicle and license plate.</p>
                <PhotoUploader files={violationPhotos} onChange={setViolationPhotos} />
              </div>

              {/* Additional Notes */}
              <div className="form-group">
                <label>Additional Notes <span className="optional">(Optional)</span></label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional details..." className="public-input" rows={3} maxLength={1000} />
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={submitLoading || !propertyId}>
                {submitLoading ? 'Submitting...' : 'Submit Report'}
              </button>
            </form>
          </>
        )}

        {/* SUCCESS */}
        {step === 'success' && (
          <div className="public-success">
            <div className="public-success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h2>Request Submitted Successfully</h2>
            <p>
              Thank you for your submission. Our dispatch team has been notified
              and will review your request promptly.
            </p>
            <p>
              If additional information is needed, a member of our team will
              reach out to you at the number provided.
            </p>
            {successCallId && (
              <div className="public-reference">Reference #{successCallId}</div>
            )}
            <button className="btn btn-primary btn-block" onClick={handleReset} style={{ marginTop: 24 }}>
              Submit Another Report
            </button>
          </div>
        )}
      </div>

      <div className="public-footer">
        <p>Apollo Towing DFW &middot; Dispatch Services</p>
      </div>
    </div>
  );
}
